import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { MAX_RANGE, MIN_RANGE } from '../../../Utils/Constants';
import { ResponsiveModal } from '@getgo/chameleon-react';
import { Button, Popover, PopoverBody } from 'reactstrap';
import RangeSelectorComponent from '../../../Components/RangeSelector/RangeSelectorComponent';
import FilterButton from '../../../Components/FilterButton/FilterButton';
import './SessionsRangeFilter.css';
import {
  trackAnalyticsFeature,
  trackAnalyticsFilterType,
  trackAnalyticsGenerateReportFeature
} from '../../../Telemetry/Mixpanel/Features/analyticsTracker';
import { ATTENDEES, FILTER, REGISTRANTS, VALUE_ADD } from '../../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import { isArrayEqual } from '../../../Utils/ArrayUtils';

export class SessionsRangeFilter extends Component {
  static propTypes = {
    isBig: PropTypes.bool.isRequired,
    uniqueId: PropTypes.string.isRequired,
    mixpanelData: PropTypes.shape({
      action: PropTypes.string.isRequired,
      reportType: PropTypes.string
    }),
    reduced_sessions: PropTypes.object,
    filterSessionsByCount: PropTypes.func
  };

  constructor(props) {
    super(props);
    this.state = {
      isRangeFilterOpen: false,
      registrantRange: [MIN_RANGE, MAX_RANGE],
      attendeeRange: [MIN_RANGE, MAX_RANGE],
      isRegistrantRangeValid: true,
      isAttendeeRangeValid: true,
      resetValid: false
    };
  }

  updateRegistrantRange = registrantRange => {
    this.setState({ registrantRange });
  };

  updateAttendeeRange = attendeeRange => {
    this.setState({ attendeeRange });
  };

  setRegistrantRangeValidity = isRegistrantRangeValid => {
    this.setState({ isRegistrantRangeValid });
  };

  setAttendeeRangeValidity = isAttendeeRangeValid => {
    this.setState({ isAttendeeRangeValid });
  };

  componentDidMount() {
    const { registrantRange, attendeeRange } = this.props.reduced_sessions.filter;

    if (
      !isArrayEqual(registrantRange, this.state.registrantRange) ||
      !isArrayEqual(attendeeRange, this.state.attendeeRange)
    ) {
      this.setState({
        registrantRange,
        attendeeRange
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { registrantRange, attendeeRange } = this.props.reduced_sessions.filter;

    if (
      !isArrayEqual(registrantRange, prevProps.reduced_sessions.filter.registrantRange) ||
      !isArrayEqual(attendeeRange, prevProps.reduced_sessions.filter.attendeeRange)
    ) {
      this.setState({
        registrantRange,
        attendeeRange
      });
    }
  }

  static contextTypes = {
    t: PropTypes.func.isRequired
  };

  toggleRangeFilter = () => {
    if (!this.state.resetValid) {
      this.setState({
        registrantRange: this.props.reduced_sessions.filter.registrantRange,
        attendeeRange: this.props.reduced_sessions.filter.attendeeRange
      });
    }
    this.setState(prevState => ({
      isRangeFilterOpen: !prevState.isRangeFilterOpen
    }));
  };

  render() {
    const { isBig, isAdmin } = this.props;
    const { sessions, filter } = this.props.reduced_sessions;
    let filterDisabled =
      sessions && ((sessions.allKeys && sessions.allKeys.length === 0) || this.props.reduced_sessions.fetching);
    let sessionsRangeFilterCount = this.getSessionsRangeFilterCount(filter);

    return (
      <div id={this.props.uniqueId}>
        <FilterButton
          isBig={isBig}
          rangeFilterCount={sessionsRangeFilterCount}
          filterDisabled={filterDisabled && !isAdmin}
          onClick={this.toggleRangeFilter}
        />
        {isBig ? this.getDefaultRender() : this.getMobileRender()}
      </div>
    );
  }

  getMobileRender = () => {
    return (
      <ResponsiveModal isOpen={this.state.isRangeFilterOpen} onClose={() => this.setResetValidity(false)}>
        <div className="sessionsRangeFilterMobile">{this.getFilters()}</div>
      </ResponsiveModal>
    );
  };

  getDefaultRender = () => {
    return (
      <Popover
        className="sessionsRangeFilterPopover"
        isOpen={this.state.isRangeFilterOpen}
        placement="bottom"
        target={this.props.uniqueId}
        toggle={this.toggleRangeFilter}
        hideArrow={true}>
        <PopoverBody>
          <div>{this.getFilters(true)}</div>
        </PopoverBody>
      </Popover>
    );
  };

  getSessionsRangeFilterCount = sessionsFilter => {
    let sessionsRangeFilterCount = 0;
    if (!isArrayEqual(sessionsFilter.registrantRange, [MIN_RANGE, MAX_RANGE])) {
      sessionsRangeFilterCount += 1;
    }
    if (!isArrayEqual(sessionsFilter.attendeeRange, [MIN_RANGE, MAX_RANGE])) {
      sessionsRangeFilterCount += 1;
    }
    return sessionsRangeFilterCount;
  };

  getFilters = () => {
    const { t } = this.context;
    return (
      <div>
        <div className="filterHeading">
          <div className="filterTitle">{t('buttons.filter')}</div>
          <div className="filterDescription">{t('sessions.filter.filterTitle')}</div>
          <hr />
        </div>

        <div className="filterBody">
          <RangeSelectorComponent
            sectionTitle={t('sessions.filter.numberOfRegistrants')}
            range={this.state.registrantRange}
            updateRange={this.updateRegistrantRange}
            className={'Registrants'}
            MIN_RANGE={MIN_RANGE}
            MAX_RANGE={MAX_RANGE}
            setValidity={this.setRegistrantRangeValidity}
          />
          <hr />
          <RangeSelectorComponent
            sectionTitle={t('sessions.filter.numberOfAttendees')}
            range={this.state.attendeeRange}
            updateRange={this.updateAttendeeRange}
            className={'Attendees'}
            MIN_RANGE={MIN_RANGE}
            MAX_RANGE={MAX_RANGE}
            setValidity={this.setAttendeeRangeValidity}
          />
        </div>
        <div className="filterSubmissionSection">
          {this.resetButton()}
          <div className="applyAndCancel">
            {this.cancelButton()}
            {this.applyButton(false)}
          </div>
        </div>
      </div>
    );
  };

  static isResetDisabled(attendeeRange, registrantRange) {
    return (
      MIN_RANGE === attendeeRange[0] &&
      MAX_RANGE === attendeeRange[1] &&
      MIN_RANGE === registrantRange[0] &&
      MAX_RANGE === registrantRange[1]
    );
  }

  resetButton = () => {
    let { attendeeRange, registrantRange } = this.state;
    const { t } = this.context;
    return (
      <Button
        className="resetButton"
        color="link"
        disabled={SessionsRangeFilter.isResetDisabled(attendeeRange, registrantRange)}
        onClick={this.reset}>
        {t('buttons.reset')}
      </Button>
    );
  };

  cancelButton = () => {
    const { t } = this.context;
    return (
      <Button onClick={() => this.setResetValidity(false)} color="link">
        {t('buttons.cancel')}
      </Button>
    );
  };

  applyButton = () => {
    let isInvalid = !this.state.isRegistrantRangeValid || !this.state.isAttendeeRangeValid;
    let { registrantRange, attendeeRange } = this.props.reduced_sessions.filter;

    let isDisabled =
      isInvalid ||
      (isArrayEqual(this.state.registrantRange, registrantRange) &&
        isArrayEqual(this.state.attendeeRange, attendeeRange));
    const { t } = this.context;
    return (
      <Button className="applyButton" disabled={isDisabled} onClick={this.applyFilter} color={'link'}>
        {t('buttons.apply')}
      </Button>
    );
  };

  setResetValidity = isValid => {
    this.setState(
      {
        resetValid: isValid
      },
      function() {
        this.toggleRangeFilter();
      }
    );
  };
  applyFilter = () => {
    const { filter } = this.props.reduced_sessions;
    const filterOptions = {
      registrantRange: this.state.registrantRange,
      attendeeRange: this.state.attendeeRange
    };
    this.props.filterSessionsByCount(filterOptions);
    this.setResetValidity(true);
    this.trackAnalytics(filter);
  };

  trackAnalytics = filter => {
    let sendEvent = false;
    if (!isArrayEqual(filter.registrantRange, this.state.registrantRange)) {
      sendEvent = true;
      trackAnalyticsFilterType(
        this.props.mixpanelData.action,
        REGISTRANTS,
        this.state.registrantRange[0],
        this.state.registrantRange[1]
      );
    }
    if (!isArrayEqual(filter.attendeeRange, this.state.attendeeRange)) {
      sendEvent = true;
      trackAnalyticsFilterType(
        this.props.mixpanelData.action,
        ATTENDEES,
        this.state.attendeeRange[0],
        this.state.attendeeRange[1]
      );
    }
    if (sendEvent) {
      trackAnalyticsFeature(VALUE_ADD, this.props.mixpanelData.action, FILTER);
      if (this.props.mixpanelData.reportType) {
        trackAnalyticsGenerateReportFeature(this.props.mixpanelData.reportType, FILTER);
      }
    }
  };

  reset = () => {
    const registrantRange = [MIN_RANGE, MAX_RANGE];
    const attendeeRange = [MIN_RANGE, MAX_RANGE];
    this.setState({
      registrantRange,
      attendeeRange,
      isRegistrantRangeValid: true,
      isAttendeeRangeValid: true,
      resetValid: false
    });
  };
}

export default SessionsRangeFilter;



// WEBPACK FOOTER //
// ./src/GoToWebinar/Sessions/Filters/SessionsRangeFilter.js